import { Label, Text } from '@pcrm/fe/ui-kit';
import type { ReactNode } from 'react';

/**
 * Un câmp de formular: etichetă, control, și — când e cazul — motivul respingerii.
 *
 * **Eroarea stă sub câmpul ei, nu într-un toast.** Un „Datele trimise nu sunt valide"
 * plutind în colț nu spune operatorului ce să corecteze, iar el se uită la douăzeci de
 * câmpuri. Toast-ul rămâne doar pentru ce nu aparține niciunui câmp (rețea, server căzut).
 *
 * Steluța marchează obligatoriul **înainte** de a apăsa Salvează. `aria-hidden` pe ea și
 * `aria-required` pe control: cititorul de ecran anunță regula, nu citește „asterisc".
 *
 * A trăit prima dată în ecranul firmelor grupului. A urcat aici când formularul de client a
 * avut nevoie de exact același lucru — a doua copie ar fi fost liberă să arate altfel, iar
 * diferența s-ar fi văzut pe ecrane vecine.
 */
export function Field({
  label,
  id,
  hint,
  error,
  suggestion,
  required = false,
  children,
}: {
  label: string;
  id: string;
  hint?: ReactNode;
  error?: string;
  /** Ce a întors ANAF pentru un câmp pe care operatorul îl scrisese deja altfel. */
  suggestion?: { value: string; onApply: () => void };
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-y-2">
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden className="text-ui-fg-error ms-0.5">
            *
          </span>
        ) : null}
      </Label>
      {children}
      {/*
        Propunerea stă **sub câmpul ei și deasupra indiciului**, nu într-un dialog de
        „ce vrei să păstrezi": operatorul le vede pe toate deodată, în context, și
        alege câmp cu câmp. Un dialog l-ar pune să decidă pentru cinci câmpuri într-o
        singură apăsare, fără să le mai aibă în față.
      */}
      {suggestion ? (
        <Text size="small" className="text-ui-fg-subtle">
          La ANAF: <span className="text-ui-fg-base">{suggestion.value}</span>{' '}
          <button
            type="button"
            className="text-ui-fg-interactive hover:underline"
            onClick={suggestion.onApply}
          >
            Înlocuiește
          </button>
        </Text>
      ) : null}
      {error ? (
        <Text size="small" className="text-ui-fg-error" role="alert">
          {error}
        </Text>
      ) : hint ? (
        <Text size="small" className="text-ui-fg-subtle">
          {hint}
        </Text>
      ) : null}
    </div>
  );
}
